import * as Sentry from '@sentry/react';
import {
  selectAppReconnecting,
  selectIceRelay,
  selectIceRelayFallback,
  selectIceRelayRequired,
} from 'features/application/applicationSlice';
import { openModal } from 'features/modal/modalSlice';
import { localFeedDisconnected, localFeedUpdated } from 'features/streaming/streamingSlice';
import { LocalFeedUpdated } from 'features/streaming/types';
import { ActiveMediaDevicePayload } from 'features/user-media/types';

import { selectAllMediaDevicePermissions } from 'features/user-media/userMediaSlice';
import { selectActiveVirtualBackground } from 'features/virtual-backgrounds/selectors';
import Janus, { JanusJS } from 'lib/janus';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import merge from 'lodash.merge';
import { store } from 'store/store';
import { eventBus } from 'utils/eventBus';
import { noop } from 'utils/flow';
import { formatErrorForLogging, logger } from 'utils/logger';
import { isError } from 'utils/types';
import { joinAsPublisherTemplate, publishTemplate, sendMessage } from 'utils/webrtc/messages';

import {
  GENERIC_JANUS_ERROR,
  JOIN_ROOM_ERROR,
  NO_PUBLISHING_FEED,
  NO_ROOM_ID,
  NO_ROOM_PIN,
} from 'utils/webrtc/errors';

import {
  FeedMediaState,
  FeedMediaStates,
  PublishingFeedStatus,
  PublishingKind,
  PublishingNegotiationMeta,
  PublishingOptions,
  PublishTemplateOptions,
  RTCClient,
} from 'utils/webrtc/index';

import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';
import { MediaController, MessageHandler } from 'utils/webrtc/publishing';
import { SignalingBus } from 'utils/webrtc/SignalingBus';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { v4 as uuidv4 } from 'uuid';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import { handleAttachError } from 'utils/webrtc/handleAttachError';
import { setOpusFmtpParams } from 'utils/webrtc/publishing/setOpusFmtpParams';
import { logJanusHandleAttached } from 'utils/webrtc/logJanusHandleAttached';
import { stopStreamTracks } from 'features/user-media/utils';
import { virtualBackgroundActivationFailed } from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { getVirtualBackgroundFailureReason } from 'utils/webrtc/virtual-background/gateErrors';
import { getDefaultVideoConstraints } from 'features/user-media/utils/getDefaultVideoConstraints';
import { IceLogger } from 'utils/webrtc/IceLogger';
import { IceRecoveryLadder } from 'utils/webrtc/IceRecoveryLadder';
import { prepareIceRestart } from 'utils/webrtc/prepareIceRestart';
import { DtlsStallRecovery } from 'utils/webrtc/DtlsStallRecovery';
import { isTurnCredentialRefused } from 'utils/webrtc/turnCredentials';

const negotiationMetaInitialValues: PublishingNegotiationMeta = {
  isNegotiating: false,
  pendingAudioBroadcastState: null,
  pendingAudioSignalingState: false,
  pendingHDSimulcast: null,
  pendingOffer: null,
  activeOffer: null,
  iceRestartAttempt: false,
  mediaFailureReported: false,
};

export abstract class BasePublishing {
  private disposed: boolean = false;

  kind: PublishingKind;

  status: PublishingFeedStatus = PublishingFeedStatus.idle;

  handle?: ControlledPublishingHandle;

  mediaController: MediaController;

  messageHandler: MessageHandler;

  signalingBus: SignalingBus;

  negotiationMeta: PublishingNegotiationMeta;

  iceRestartTimer?: number;

  iceRecovery = new IceRecoveryLadder();

  dtlsRecovery: DtlsStallRecovery;

  // Set by the never-established rebuild; consumed by handleCleanup, which re-attaches.
  private pendingRebuild = false;

  // Handle whose events we accept. A rebuild flips it on the fresh attach, so the wedged
  // handle's late hangup and `oncleanup` cannot re-enter the state machine against the new PC.
  private activeHandleId?: string;

  private isActiveFeed = (): boolean =>
    Object.is(RTCClient.publishingFeed, this) || Object.is(RTCClient.screensharingFeed, this);

  broadcastIntended: boolean = false;

  broadcasterLimit: number = 1;

  defaultMediaState: FeedMediaState = {
    enabled: false,
    allowed: false,
    broadcasting: false,
    captured: false,
  };

  mediaStates = {
    video: { ...this.defaultMediaState },
    audio: { ...this.defaultMediaState },
  };

  abstract getActiveMediaConfig: () => {
    video?: boolean | MediaTrackConstraints;
    audio?: boolean | MediaTrackConstraints;
    stream?: MediaStream;
  };

  protected abstract getSimulcastMaxBitrates: () => {
    low: number;
    medium?: number;
    high: number;
  };

  protected abstract getSendEncodings: () => Array<{
    rid: string;
    active: boolean;
    maxBitrate?: number;
    scaleResolutionDownBy?: number;
  }> | null;

  protected abstract isSimulcastEnabled: () => boolean;

  protected constructor(kind: PublishingKind) {
    this.kind = kind;

    this.mediaController = new MediaController(this);
    this.messageHandler = new MessageHandler(this);
    this.signalingBus = new SignalingBus(this.kind);
    this.negotiationMeta = {
      ...negotiationMetaInitialValues,
    };

    this.dtlsRecovery = new DtlsStallRecovery({
      feedType: kind,
      canRecover: () =>
        !!this.handle &&
        !this.disposed &&
        !RTCClient.supressErrors &&
        this.isActiveFeed() &&
        !this.negotiationMeta.mediaFailureReported,
      canRebuild: () => kind === 'publishing',
      connectionStateSupported: () => this.getPeerConnection()?.connectionState !== undefined,
      rebuild: this.rebuildOnFreshHandle,
      cancelIceRestart: this.clearIceRestartTimer,
      reportFailure: () =>
        this.reportMediaFailure('connection_failed', 'failed', { previouslyConnected: true }),
    });
  }

  private get uiState(): LocalFeedUpdated {
    const {
      status,
      mediaStates: {
        video: { enabled: videoEnabled, broadcasting: videoBroadcasting },
        audio: { enabled: audioEnabled, broadcasting: audioBroadcasting },
      },
    } = this;

    const streams = Object.values(this.mediaController.streams).map((stream) => ({
      kind: stream.kind,
      id: stream.id,
    }));

    return {
      feedId: this.handle?.feedId,
      status,
      audioEnabled,
      videoEnabled,
      audioBroadcasting,
      videoBroadcasting,
      streams,
    };
  }

  // public interface
  startVideoBroadcasting = async () => {
    if (this.mediaStates.video.broadcasting) {
      return;
    }

    this.mediaStates.video.enabled = true;
    this.mediaStates.video.broadcasting = true;

    this.updateRedux();

    const selectedVB = selectActiveVirtualBackground(store.getState());
    if (selectedVB.value) {
      const streamVB = await this.activateVirtualBackground(selectedVB);

      if (streamVB) {
        await this.requestPublish({
          stream: streamVB,
          media: {
            replaceVideo: true,
            replaceAudio: this.mediaStates.audio.enabled,
          },
        });

        return;
      }
    }

    await this.requestPublish();
  };

  stopVideoBroadcasting = async () => {
    if (!this.mediaStates.video.broadcasting) {
      return;
    }

    this.mediaStates.video.enabled = false;
    this.mediaStates.video.broadcasting = false;
    this.mediaStates.video.captured = false;

    this.updateRedux();

    if (this.kind === 'publishing') {
      await VirtualBackgroundManager.deactivate();
    }

    if (this.handle && this.kind === 'publishing') {
      const videoStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'video'
      );

      this.signalingBus.addMessage('stop', this.handle.feedId, videoStreams);
    }

    await this.requestPublish({
      media: {
        removeVideo: true,
      },
    });
  };

  toggleVideoBroadcasting = async () => {
    logger
      .remote({ action: true })
      .info(
        `Video toggled by user, from ${
          this.mediaStates.video.broadcasting ? 'enabled' : 'disabled'
        } to ${this.mediaStates.video.broadcasting ? 'disabled' : 'enabled'}`
      );

    if (this.mediaStates.video.broadcasting) {
      await this.stopVideoBroadcasting();
    } else {
      await this.startVideoBroadcasting();
    }
  };

  startAudioBroadcasting = async () => {
    if (this.mediaStates.audio.broadcasting) {
      return;
    }

    this.mediaStates.audio.broadcasting = true;

    if (this.handle) {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );
      this.signalingBus.addMessage('start', this.handle.feedId, audioStreams);
    }

    this.updateRedux();

    await this.updateAudioBroadcasting();
  };

  stopAudioBroadcasting = async (unpublish?: boolean) => {
    if (!this.mediaStates.audio.broadcasting) {
      return;
    }

    this.mediaStates.audio.broadcasting = false;

    if (this.handle && this.kind === 'publishing') {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );
      this.signalingBus.addMessage('stop', this.handle.feedId, audioStreams);
    }

    if (unpublish) {
      this.mediaStates.audio.enabled = false;

      await this.requestPublish({
        media: {
          audioSend: false,
          removeAudio: true,
        },
      });
    } else {
      await this.updateAudioBroadcasting();
    }

    this.updateRedux();
  };

  toggleAudioBroadcasting = async () => {
    logger
      .remote({ action: true })
      .info(
        `Audio toggled by user, from ${
          this.mediaStates.audio.broadcasting ? 'enabled' : 'disabled'
        } to ${this.mediaStates.audio.broadcasting ? 'disabled' : 'enabled'}`
      );

    this.mediaStates.audio.broadcasting = !this.mediaStates.audio.broadcasting;

    const type = this.mediaStates.audio.broadcasting ? 'start' : 'stop';

    if (this.handle) {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );

      if (audioStreams.length) {
        this.signalingBus.addMessage(type, this.handle.feedId, audioStreams);
      }
      // we've enabled audio but there's no audio stream yet.
      // remember to post an update when it appears
      else if (this.mediaStates.audio.broadcasting) {
        this.negotiationMeta.pendingAudioSignalingState = true;
      }
    }

    this.updateRedux();
    await this.updateAudioBroadcasting();
  };

  changeAudioDevice = async (device: ActiveMediaDevicePayload) => {
    await this.requestPublish({
      media: {
        replaceAudio: this.mediaStates.audio.enabled,
        audio: {
          deviceId: {
            exact: device.id,
          },
        },
      },
    });
  };

  changeVideoDeviceWithVB = async (device: ActiveMediaDevicePayload) => {
    const activeMediaConfig = this.getActiveMediaConfig();
    const originalStream = await this.mediaController.requestExternalStream({
      audio: activeMediaConfig.audio,
      video: { deviceId: { exact: device.id } },
    });

    const externalStream = await VirtualBackgroundManager.updateStream({
      stream: originalStream,
    });

    if (!externalStream) {
      stopStreamTracks(originalStream);
      return;
    }

    // Safe to stop: Manager cloned both video and audio tracks
    stopStreamTracks(originalStream);

    await this.requestPublish({
      stream: externalStream,
      media: {
        replaceVideo: true,
        replaceAudio: this.mediaStates.audio.enabled,
      },
    });
  };

  changeVideoDevice = async (device: ActiveMediaDevicePayload) => {
    if (VirtualBackgroundManager.activated) {
      await this.changeVideoDeviceWithVB(device);
      return;
    }

    await this.requestPublish({
      media: {
        replaceVideo: this.mediaStates.video.enabled,
        video: {
          ...getDefaultVideoConstraints(),
          deviceId: {
            exact: device.id,
          },
        },
      },
    });
  };

  getStream = (id: string) => this.mediaController.streams[id];

  cleanupStreams = () => {
    Object.keys(this.mediaController.streams).forEach((id) => {
      this.mediaController.removeStream(id);
    });
  };

  cleanupUIStreams = () => {
    if (this.disposed) {
      return;
    }

    const currentState = this.uiState;
    const clearedStreamsState = {
      ...currentState,
      streams: [],
    };

    store.dispatch(localFeedUpdated({ kind: this.kind, data: clearedStreamsState }));
  };

  toggleSimulcastHDLayer = async () => {
    if (!this.handle) {
      return;
    }

    if (!this.getPeerConnection()) {
      this.negotiationMeta.pendingHDSimulcast = !this.negotiationMeta.pendingHDSimulcast;
      return;
    }

    this.toggleHDLayer();
  };

  // internals
  // janus controls
  attachPlugin = () => {
    this.dtlsRecovery.onNewPeerConnection();

    const connection = RTCClient.mediaServerConnector.connections.publishing;

    const videoroomHandle = uuidv4();
    this.activeHandleId = videoroomHandle;

    const forActiveHandle =
      <A extends unknown[]>(handler: (...args: A) => void) =>
      (...args: A) => {
        if (this.activeHandleId === videoroomHandle) {
          handler(...args);
        }
      };

    if (this.status === PublishingFeedStatus.idle) {
      this.status = PublishingFeedStatus.connecting;
    }

    connection.janus.attach({
      plugin: 'janus.plugin.videoroom',
      opaqueId: connection.handle,
      success: async (videoroom) => {
        if (this.activeHandleId !== videoroomHandle) {
          videoroom.detach();
          return;
        }

        this.status = PublishingFeedStatus.connected;
        this.updateRedux();

        this.handle = { videoroom, handle: videoroomHandle, streams: {} };

        logJanusHandleAttached(this.kind, videoroom, connection.janus);

        await this.joinRoom();
      },
      oncleanup: forActiveHandle(this.handleCleanup),
      error: handleAttachError(`attach_publishing_feed_${videoroomHandle}`),
      consentDialog: noop,
      webrtcState: forActiveHandle((on, reason) => {
        Janus.log(`${this.kind} feed: WebRTC PeerConnection is ${on ? 'up' : 'down'} now`);

        if (on) {
          this.dtlsRecovery.onWebRTCUp();
        }

        if (
          on &&
          this.kind === 'publishing' &&
          Janus.webRTCAdapter.browserDetails.browser === 'firefox'
        ) {
          this.handle?.videoroom.send({
            message: {
              request: 'configure',
              bitrate: this.getSimulcastMaxBitrates().medium,
            },
          });
        }

        if (!on) {
          this.onWebRTCConnectionDown(reason);
        }
      }),
      signalingState: forActiveHandle(this.onSignalingState),
      mediaState: forActiveHandle(this.onMediaState),
      onmessage: forActiveHandle(this.messageHandler.onMessage),
      onlocaltrack: forActiveHandle(async (track, on) => {
        if (!on) {
          await this.mediaController.handleRemoveTrack(track);
          return;
        }
        this.mediaController.addStream(track);
        this.updateRedux();
      }),
      iceState: forActiveHandle(this.onIceState),
      connectionState: forActiveHandle(this.onConnectionState),
      iceGatheringState: forActiveHandle(this.onIceGatheringState),
      // Don't consume tracks in publishing feed
      onremotetrack: () => {},
      sframe: SFrameManager.getClient(),
    });
  };

  requestPublish = async (offerParams?: JanusJS.OfferParams) => {
    const feed = this.handle;

    if (!feed) {
      throw NO_PUBLISHING_FEED(this.kind);
    }

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    // prevent attempt to publish without devices
    if (!this.getPeerConnection() && !videoEnabled && !audioEnabled) {
      RTCClient.resetPublishOptions();

      return;
    }

    const offer = await this.prepareOffer(offerParams);

    // save upcoming request during existing negotiation
    if (this.negotiationMeta.isNegotiating) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('Negotiation in progress, queueing upcoming requests...', offer);

      this.negotiationMeta.pendingOffer = offer;
      return;
    }

    this.negotiationMeta.isNegotiating = true;
    this.negotiationMeta.activeOffer = offer;

    feed.videoroom.createOffer(offer);
  };

  handleCleanup = async () => {
    this.clearIceRestartTimer();
    this.dtlsRecovery.onPeerConnectionGone();

    if (this.disposed) {
      this.pendingRebuild = false;
      this.cleanupStreams();
      return;
    }

    if (this.kind === 'publishing') {
      await VirtualBackgroundManager.deactivate();
    }

    this.cleanupStreams();

    delete this.handle;

    // The PC is gone: a fresh budget for the next publish. Both per-PC latches go
    // with it, or restore() bails forever and the next teardown reports a false cause.
    this.iceRecovery = new IceRecoveryLadder();
    this.negotiationMeta.mediaFailureReported = false;
    this.negotiationMeta.iceRestartAttempt = false;

    const isReconnecting = selectAppReconnecting(store.getState());
    if (isReconnecting) {
      this.reconnectionReset();
    }

    this.updateRedux();

    logger.debug(`${this.kind} feed: cleaned up connection`);

    const rebuildPending = this.pendingRebuild;
    this.pendingRebuild = false;

    if (rebuildPending && !isReconnecting) {
      this.attachPlugin();
    } else {
      // No fresh PC is coming, so the rebuild deadline has nothing left to time out.
      this.dtlsRecovery.abandonRebuild();
    }
  };

  cleanupConnection = ({ detach = false }: { detach?: boolean } = {}) => {
    logger.debug(`${this.kind} feed: cleaning up connection`);
    const feed = this.handle;

    if (!feed || RTCClient.isMediaServerError) {
      this.handleCleanup();
    }

    if (!feed) {
      return;
    }

    if (feed.feedId) {
      RTCClient.disconnectFeed(feed.feedId);
    }

    if (detach) {
      feed.videoroom.detach();
      return;
    }

    feed.videoroom.hangup();
  };

  hangup = (stopSignalingStreams?: boolean) => {
    this.status = PublishingFeedStatus.idle;

    // A hangup can land before an in-flight rebuild's own cleanup arrives; clearing the latch
    // here stops handleCleanup from re-attaching a handle the user just asked to close.
    this.pendingRebuild = false;

    if (stopSignalingStreams) {
      this.stopSignalingStreams();
    }

    // TODO: We might need to move resetMediaStates to `cleanupConnection`
    this.resetMediaStates();
    this.cleanupConnection();
  };

  dispose = () => {
    this.disposed = true;
    this.clearIceRestartTimer();
    this.dtlsRecovery.onPeerConnectionGone();
    this.dtlsRecovery.abandonRebuild();
  };

  // `undefined` doubles as "no restart pending" for the ladder gate in restore().
  private clearIceRestartTimer = () => {
    window.clearTimeout(this.iceRestartTimer);
    this.iceRestartTimer = undefined;
  };

  updateStreamingPermissions = () => {
    const mediaPermissions = selectAllMediaDevicePermissions(store.getState());

    this.mediaStates.audio.allowed = mediaPermissions.audioinput === 'granted';
    this.mediaStates.video.allowed = mediaPermissions.videoinput === 'granted';

    this.updateRedux();
  };

  // @TODO: Refactor this method. Enhance flexibility to handle external configuration changes.
  // Consider implementing a more robust configuration interface that can adapt
  // to changes without requiring internal code modifications.
  // the expected concept in this reverted commit because of regression 7d2704a4fae70ba360118f9bfb7fceb0cc905b32
  configureStreamingState = (options: PublishingOptions = {}) => {
    const mediaPermissions = selectAllMediaDevicePermissions(store.getState());

    this.mediaStates.video.allowed = mediaPermissions.videoinput === 'granted';
    this.mediaStates.audio.allowed = mediaPermissions.audioinput === 'granted';

    this.mediaStates.video.enabled = this.mediaStates.video.allowed && !!options.video;
    this.mediaStates.audio.enabled = this.mediaStates.audio.allowed && !!options.audio;

    this.mediaStates.video.broadcasting = this.mediaStates.video.enabled;
    this.mediaStates.audio.broadcasting = this.mediaStates.audio.enabled;

    this.updateRedux();
  };

  publish = async (options: PublishTemplateOptions, jsep: JanusJS.JSEP) => {
    if (!this.handle) {
      return;
    }

    await sendMessage(this.handle.videoroom, publishTemplate(options), jsep);
  };

  // janus events

  onSignalingState = async (state: RTCSignalingState) => {
    logger.remote({ system: true, capture: 'streaming' }).debug('Signaling state:', state);

    if (state === 'stable' || state === 'closed') {
      this.negotiationMeta.isNegotiating = false;

      if (this.negotiationMeta.pendingOffer) {
        const pendingOffer = cloneDeep(this.negotiationMeta.pendingOffer);
        this.negotiationMeta.pendingOffer = null;

        const negotiationRequired = !isEqual(this.negotiationMeta.activeOffer, pendingOffer);

        if (negotiationRequired) {
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug('Requesting negotiation with queued offer...', pendingOffer);
          await this.requestPublish(pendingOffer);
        }
      }

      this.negotiationMeta.activeOffer = null;
    } else {
      this.negotiationMeta.isNegotiating = true;
    }
  };

  onMediaState = async (medium: 'audio' | 'video', on: boolean, mid?: string) => {
    logger
      .remote()
      .debug(`Janus ${on ? 'started' : 'stopped'} receiving our ${medium} (mid=${mid})`);

    const feed = this.handle;

    if (!feed) {
      return;
    }

    feed.streams = feed.streams || {};

    if (on) {
      RTCClient.resetPublishOptions();

      if (mid) {
        if (medium === 'audio') {
          if (this.mediaStates.audio.broadcasting) {
            feed.videoroom.unmuteAudio(mid);
          } else {
            feed.videoroom.muteAudio(mid);
          }
          this.negotiationMeta.pendingAudioBroadcastState = null;
        }

        feed.streams[mid] = { type: medium, mid, on: true };

        if (medium === 'audio' && this.negotiationMeta.pendingAudioSignalingState) {
          this.resolvePendingAudioSignalingState();
        }
      }

      if (!feed.connected && !feed.connecting) {
        feed.connecting = true;
      }
    } else if (mid && feed.streams[mid]) {
      feed.streams[mid].on = false;
    }

    const shouldSendUpdate = on ? !this.mediaStates[medium].captured : false;

    if (mid && shouldSendUpdate) {
      // Only send 'start' if the media is actually intended to be broadcasting
      const shouldBroadcast = on ? this.mediaStates[medium].broadcasting : true;

      if (shouldBroadcast) {
        this.signalingBus.addMessage(on ? 'start' : 'stop', feed.feedId, [{ type: medium, mid }]);
      }

      if (on) {
        this.mediaStates[medium].captured = true;

        if (medium === 'video') {
          if (this.negotiationMeta.pendingHDSimulcast) {
            this.resolvePendingHDSimulcast();
          }
        }
      }
    }
  };

  onIceState = (state: RTCIceConnectionState, stats?: JanusJS.IceConnectionStats) => {
    if (!this.handle?.handle) {
      return;
    }

    // Superseded by Client.reset(): the PC is orphaned and would falsely classify
    // as network_blocked, so drop the event before any log, Sentry or modal fires.
    if (!this.isActiveFeed()) {
      return;
    }

    if (this.kind === 'screensharing') {
      logger.remote().debug(`[SCREENSHARE-DEBUG] ICE state: ${state}`, { hidden: document.hidden });
    }

    const peerConnection = this.getPeerConnection();

    if ((state === 'connected' || state === 'completed') && stats) {
      IceLogger.logConnectionSuccess(this.handle.handle, this.kind, stats, peerConnection);
    } else if (state === 'failed' || state === 'disconnected') {
      IceLogger.logConnectionFailure(this.handle.handle, this.kind, state, stats);
    } else {
      IceLogger.logConnectionState(this.handle.handle, this.kind, state);
    }

    const restore = (reason: 'disconnected' | 'failed') => {
      if (this.negotiationMeta.mediaFailureReported) {
        return;
      }

      // A pending restart already owns this death; `disconnected` → `failed`
      // for one outage must not walk the ladder twice.
      if (this.iceRestartTimer !== undefined) {
        return;
      }

      // ICE up, DTLS never completed: the ladder's restart cannot help.
      if (this.dtlsRecovery.onIceDisconnected()) {
        return;
      }

      const decision = this.iceRecovery.decide(reason, stats?.previouslyConnected ?? false);

      if (decision.action === 'report') {
        this.reportMediaFailure(decision.cause, state, stats, decision.connectedLifetimeMs);
        return;
      }

      this.iceRestartTimer = window.setTimeout(async () => {
        this.iceRestartTimer = undefined;

        if (this.disposed || !this.handle || this.negotiationMeta.mediaFailureReported) {
          return;
        }

        // Read before the await: handleCleanup can drop the handle while the
        // restart is in flight.
        const handleId = this.handle.handle;

        logger
          .remote({ system: true, capture: 'streaming' })
          .warn(`${this.kind} peer connection ${reason}. Trying to recover...`, {
            handleId,
            relay: decision.relay,
            // prepareIceRestart skips relay silently when the flag is off, so without
            // this a disabled run is indistinguishable from a rung that declined relay.
            relayFallbackEnabled: selectIceRelayFallback(store.getState()),
            connectedLifetimeMs: decision.connectedLifetimeMs,
            stableReset: decision.stableReset,
            ...this.iceRecovery.snapshot(),
          });

        prepareIceRestart({
          pc: this.getPeerConnection(),
          ladder: this.iceRecovery,
          decision,
          handleId,
          feedType: this.kind,
          reason,
        });

        this.negotiationMeta.iceRestartAttempt = true;

        try {
          await this.requestPublish({ iceRestart: true });
        } catch (error) {
          if (RTCClient.supressErrors) {
            return;
          }

          const message = 'ICE restart request failed';
          const context = {
            handleId,
            direction: 'publishing',
            reason,
          };

          logger.remote({ system: true, capture: 'streaming' }).error(message, context);

          Sentry.captureException(new Error(message, { cause: error }), {
            tags: { component: 'BasePublishing', operation: 'ice-restart' },
            contexts: { iceRestart: { ...context, ...formatErrorForLogging(error) } },
          });
        }
      }, 5000);
    };

    if (state === 'connected' || state === 'completed') {
      this.dtlsRecovery.onIceConnected();

      // A rung is spent only by a restart that fired; cancelling a pending one
      // must hand its rung back.
      if (this.iceRestartTimer !== undefined) {
        this.iceRecovery.forgiveUndispatchedDeath();
      }

      this.clearIceRestartTimer();
      this.negotiationMeta.iceRestartAttempt = false;
      this.iceRecovery.markConnected();

      // Engines pass through 'connected' before 'completed'; log the relay once.
      if (state === 'connected' && this.iceRecovery.isRelayActive) {
        IceLogger.logRelayConnected(this.handle.handle, this.kind, peerConnection);
      }
    }

    if (state === 'disconnected' || state === 'failed') {
      restore(state);
    }
  };

  onConnectionState = (state: RTCPeerConnectionState) => {
    if (!this.handle?.handle) {
      return;
    }

    if (!this.isActiveFeed()) {
      return;
    }

    this.dtlsRecovery.onConnectionState(state);
  };

  // A latch rather than a direct re-attach: handleCleanup deletes the handle, so it is the one
  // place that can attach the successor. `this.hangup()` is avoided here — it resets mediaStates.
  private rebuildOnFreshHandle = async (): Promise<boolean> => {
    // The teardown below stops this stream's tracks, and a re-offer would publish the ended ones.
    RTCClient.resetPublishOptions();

    this.pendingRebuild = true;

    // Detaching, not hanging up: a handle torn down only locally stays joined server-side, keeps
    // Janus's DTLS deadline running and holds the publisher slot the rebuilt handle needs.
    this.cleanupConnection({ detach: true });

    return true;
  };

  onIceGatheringState = (state: RTCIceGatheringState, candidateStats?: any) => {
    if (!this.handle?.handle) {
      return;
    }

    if (state !== 'complete') {
      return;
    }

    const peerConnection = this.getPeerConnection();
    const iceServers = peerConnection?.getConfiguration()?.iceServers;
    IceLogger.logGatheringComplete(
      this.handle.handle,
      this.kind,
      candidateStats,
      iceServers,
      peerConnection
    );

    // Chrome enumerates host/mDNS candidates with no network access, so exactly 0
    // proves the PC is wedged in 'new' with no death for onIceState to react to.
    if (candidateStats?.gathered !== 0) {
      return;
    }

    if (RTCClient.supressErrors || !this.isActiveFeed()) {
      return;
    }

    // A renegotiation can re-gather nothing (Firefox bundling a new m-line), and a live PC keeps
    // its join-time credential: neither the empty gathering nor that expiry means it failed.
    const iceState = peerConnection?.iceConnectionState;
    if (iceState === 'connected' || iceState === 'completed') {
      return;
    }

    const { hasConnected } = this.iceRecovery;
    // An empty restart is the ladder's to retry only on the PC that connected (a fresh PC parks in
    // 'new'); a refused/expired TURN credential ends recovery anyway, as Rejoin gets a fresh one.
    if (
      this.dtlsRecovery.iceEverConnected &&
      !isTurnCredentialRefused(candidateStats.candidateErrors, iceServers)
    ) {
      return;
    }

    this.reportMediaFailure(
      hasConnected ? 'connection_failed' : 'network_blocked',
      iceState ?? 'new',
      {
        gathered: 0,
        nonHostGathered: 0,
        previouslyConnected: hasConnected,
        candidateErrors: candidateStats.candidateErrors,
      }
    );
  };

  // Callers must gate on `RTCClient.supressErrors`; the helper only dedupes
  // via `mediaFailureReported`.
  reportMediaFailure = (
    cause: 'connection_failed' | 'network_blocked',
    state: RTCIceConnectionState,
    stats?: Partial<JanusJS.IceConnectionStats>,
    connectedLifetimeMs: number | null = null
  ) => {
    if (this.negotiationMeta.mediaFailureReported) {
      return;
    }

    const { handle } = this;
    if (!handle) {
      return;
    }

    const direction = 'publishing' as const;
    const pluginType = this.kind;
    const handleId = handle.handle;
    const iceRelay = selectIceRelay(store.getState());
    const message = 'Publishing peer connection failed';
    const iceFallback = { ...this.iceRecovery.snapshot(), connectedLifetimeMs };
    const turnCredentialRefused = isTurnCredentialRefused(
      stats?.candidateErrors,
      this.getPeerConnection()?.getConfiguration()?.iceServers
    );
    // A refused or expired TURN credential is not a network block: Rejoin fetches a fresh one.
    const reportedCause =
      cause === 'network_blocked' && turnCredentialRefused ? 'connection_failed' : cause;

    const candidatesGathered = stats?.gathered ?? 0;
    const nonHostCandidatesGathered = stats?.nonHostGathered ?? 0;
    const previouslyConnected = stats?.previouslyConnected ?? false;

    logger.remote({ capture: 'streaming' }).error(message, {
      handleId,
      pluginType,
      direction,
      iceConnectionState: state,
      candidatesGathered,
      nonHostCandidatesGathered,
      previouslyConnected,
      iceCompleted: stats?.iceCompleted ?? false,
      durationMs: stats?.duration,
      iceRelay,
      iceFallback,
      cause: reportedCause,
      turnCredentialRefused,
    });

    Sentry.captureException(new Error(message), {
      contexts: {
        mediaFailure: {
          cause: reportedCause,
          turnCredentialRefused,
          direction,
          pluginType,
          iceRelay,
          iceConnectionState: state,
          candidatesGathered,
          nonHostCandidatesGathered,
          previouslyConnected,
          iceCompleted: stats?.iceCompleted ?? false,
          durationMs: stats?.duration,
          handleId,
        },
        iceFallback,
      },
    });

    eventBus.sendMessage('mediaConnectionFailed', {}, { direction, cause: reportedCause });
    store.dispatch(openModal('mediaConnectionFailed', { cause: reportedCause, direction }));

    this.negotiationMeta.mediaFailureReported = true;
  };

  // Janus tears the PC down before Chrome re-emits a terminal `iceConnectionState`, and its
  // `reason` is the only server-side verdict on a PC that never completed DTLS.
  onWebRTCConnectionDown = (rawReason?: string) => {
    if (RTCClient.supressErrors) {
      return;
    }

    // Superseded by Client.reset(): drop the event so this orphan can't
    // open the modal when Janus tears down its handle.
    if (!this.isActiveFeed()) {
      return;
    }

    // After the teardown guards on purpose: leave/reset hang up every handle.
    IceLogger.logPeerConnectionDown(this.handle?.handle, this.kind, {
      reason: rawReason,
      iceRestartAttempt: this.negotiationMeta.iceRestartAttempt,
      mediaFailureReported: this.negotiationMeta.mediaFailureReported,
    });

    this.dtlsRecovery.routeHangup(rawReason, this.negotiationMeta);
  };

  updateRedux = () => {
    if (this.disposed) {
      return;
    }

    const state = this.uiState;
    store.dispatch(localFeedUpdated({ kind: this.kind, data: state }));
  };

  resetMediaStates = (media?: 'video' | 'audio') => {
    if (media) {
      this.mediaStates[media] = { ...this.defaultMediaState };
    } else {
      this.mediaStates.video = { ...this.defaultMediaState };
      this.mediaStates.audio = { ...this.defaultMediaState };
    }
  };

  reconnectionReset = () => {
    this.mediaStates.video.captured = false;
    this.mediaStates.audio.captured = false;
    this.status = PublishingFeedStatus.idle;
    this.negotiationMeta = {
      ...negotiationMetaInitialValues,
    };

    store.dispatch(localFeedDisconnected({ kind: this.kind }));
  };

  blockAudioPublishing = async () => {
    logger.remote().log('Audio publishing blocked');

    this.mediaStates.audio.allowed = false;
    this.mediaStates.audio.captured = false;

    if (this.mediaStates.audio.enabled) {
      await this.stopAudioBroadcasting(true);
    } else {
      this.updateRedux();
    }
  };

  blockVideoPublishing = async () => {
    logger.remote().log('Video publishing blocked');

    this.mediaStates.video.allowed = false;
    this.mediaStates.video.captured = false;

    if (this.mediaStates.video.enabled) {
      await this.stopVideoBroadcasting();
    } else {
      this.updateRedux();
    }
  };

  stopSignalingStreams = () => {
    if (this.handle) {
      this.signalingBus.addMessage('stop', this.handle.feedId, Object.values(this.handle.streams));
    }
  };

  getMediaStates = (): FeedMediaStates => ({
    audio: this.mediaStates.audio,
    video: this.mediaStates.video,
  });

  changeVirtualBackground = async (config: SelectedVirtualBackground): Promise<boolean> => {
    if (VirtualBackgroundManager.activated) {
      logger
        .remote({ action: true, capture: 'virtual-background' })
        .log('The user changed virtual background', config);

      await VirtualBackgroundManager.changeBackground(config);
      return true;
    }

    const streamVB = await this.activateVirtualBackground(config);
    if (!streamVB) {
      return false;
    }

    if (this.mediaStates.video.broadcasting) {
      logger
        .remote({ capture: 'virtual-background' })
        .log('The user applied virtual background', config);

      await this.requestPublish({
        stream: streamVB,
        media: { replaceVideo: this.mediaStates.video.enabled },
      });
    } else {
      await VirtualBackgroundManager.deactivate();
      stopStreamTracks(streamVB);
    }

    return true;
  };

  // janus events

  resetVirtualBackground = async () => {
    logger.remote({ action: true }).log('The user disabled virtual background');

    await VirtualBackgroundManager.deactivate();

    if (!this.mediaStates.video.broadcasting) {
      return;
    }

    await this.requestPublish({ media: { replaceVideo: true } });
  };

  stopBroadcasting = () => {
    this.stopVideoBroadcasting();
    this.stopAudioBroadcasting();
  };

  activateVirtualBackground = async (
    config: SelectedVirtualBackground,
    params: { stream?: MediaStream } = {}
  ): Promise<MediaStream | undefined> => {
    logger.remote().log('Applying virtual background', config);

    let externalStream: MediaStream | undefined;
    try {
      externalStream = params.stream || (await this.mediaController.requestExternalStream());

      const resultedStream = await VirtualBackgroundManager.activateBackground({
        type: config.type,
        value: config.value,
        stream: externalStream,
      });

      if (!resultedStream) {
        if (!params.stream) {
          stopStreamTracks(externalStream);
        }
        return undefined;
      }

      // Safe to stop: Manager cloned both video and audio tracks
      if (!params.stream) {
        stopStreamTracks(externalStream);
      }

      return resultedStream;
    } catch (error) {
      if (externalStream && !params.stream) {
        stopStreamTracks(externalStream);
      }
      const reason = getVirtualBackgroundFailureReason(error);
      if (reason) {
        store.dispatch(virtualBackgroundActivationFailed({ reason, requested: config }));
      }
      return undefined;
    }
  };

  getPeerConnection = (): RTCPeerConnection | undefined => this.handle?.videoroom?.webrtcStuff?.pc;

  private updateAudioBroadcasting = async () => {
    const { enabled: audioEnabled, allowed: audioAllowed } = this.mediaStates.audio;

    if (audioAllowed && !audioEnabled) {
      this.mediaStates.audio.enabled = true;

      this.updateRedux();

      await this.requestPublish({ media: { audioSend: true } });
    } else if (this.handle) {
      // don't re-negotiate anymore to keep instant feedback and better performance
      const mid = Object.values(this.handle.streams).find((stream) => stream.type === 'audio')?.mid;
      if (mid) {
        // drive the mute state from the broadcasting flag, not the live track
        if (this.mediaStates.audio.broadcasting) {
          this.handle.videoroom.unmuteAudio(mid);
        } else {
          this.handle.videoroom.muteAudio(mid);
        }
      } else {
        // no mid yet, awaiting media state update
        this.negotiationMeta.pendingAudioBroadcastState = this.mediaStates.audio.broadcasting;
      }
    }
  };

  private joinRoom = async () => {
    const { roomId, roomPin } = RTCClient;
    const feed = this.handle;

    if (!roomId) {
      throw NO_ROOM_ID();
    }
    if (!roomPin) {
      throw NO_ROOM_PIN();
    }

    if (!feed) {
      return;
    }

    try {
      await sendMessage(feed.videoroom, joinAsPublisherTemplate(roomId, roomPin));
    } catch (error) {
      if (isError(error)) {
        Sentry.captureException(error);
        throw JOIN_ROOM_ERROR(error.message);
      } else {
        throw error;
      }
    }
  };

  private shouldRestoreVirtualBackground = (offerParams: JanusJS.OfferParams) => {
    const selectedVB = selectActiveVirtualBackground(store.getState());

    return !offerParams.stream && !VirtualBackgroundManager.activated && selectedVB.value;
  };

  private restoreVirtualBackground = async () => {
    const selectedVB = selectActiveVirtualBackground(store.getState());

    try {
      return await this.activateVirtualBackground(selectedVB);
    } catch (error) {
      Sentry.captureException(error);
      logger.remote().error('Failed to restore virtual background:', error);

      return undefined;
    }
  };

  private prepareOffer = async (offerParams?: JanusJS.OfferParams) => {
    const activeMediaConfig = this.getActiveMediaConfig();

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    const params: JanusJS.OfferParams = merge(
      {
        // Only a PC-creating offer reads this; a remembered relay decision must not
        // race the direct path again.
        iceTransportPolicy: selectIceRelayRequired(store.getState()) ? 'relay' : undefined,
        simulcast: this.isSimulcastEnabled(),
        simulcastMaxBitrates: this.getSimulcastMaxBitrates(),
        sendEncodings: this.getSendEncodings(),
        media: {
          audioRecv: false,
          videoRecv: false,
          audioSend: audioEnabled,
          videoSend: videoEnabled,
          audio: audioEnabled ? activeMediaConfig.audio : undefined,
          video: videoEnabled ? activeMediaConfig.video : undefined,
        },
        customizeSdp: (jsep: JanusJS.JSEP) => {
          jsep.sdp = setOpusFmtpParams(jsep.sdp, { usedtx: 1 });
        },
        success: (jsep: JanusJS.JSEP) =>
          this.publish(
            {
              audio: offerParams?.media?.audioSend ?? audioEnabled,
              video: offerParams?.media?.videoSend ?? videoEnabled,
            },
            jsep
          ),
        error: GENERIC_JANUS_ERROR(`publish_local_feed`),
      },
      offerParams
    );

    const offer = cloneDeep(params);

    // use the stream from join screen for the first publishing
    if (this.kind === 'publishing' && !this.getPeerConnection()) {
      if (RTCClient.publishOptions.stream) {
        offer.stream = RTCClient.publishOptions.stream;
      }
    }

    if (videoEnabled) {
      if (this.kind === 'publishing') {
        // check whether we need to restore the virtual background stream
        // since we lose access to the previous stream on reconnect
        if (this.shouldRestoreVirtualBackground(offer)) {
          offer.stream = await this.restoreVirtualBackground();
        }
      }

      if (this.kind === 'screensharing') {
        offer.stream = activeMediaConfig.stream;
      }
    }

    return offer;
  };

  private resolvePendingAudioSignalingState = () => {
    this.negotiationMeta.pendingAudioSignalingState = false;

    if (!this.handle) {
      return;
    }

    if (!this.mediaStates.audio.broadcasting) {
      return;
    }

    const audioStreams = Object.values(this.handle.streams).filter(
      (stream) => stream.type === 'audio'
    );

    if (audioStreams.length) {
      this.signalingBus.addMessage('start', this.handle.feedId, audioStreams);
    }
  };

  private resolvePendingHDSimulcast = () => {
    this.toggleHDLayer();

    this.negotiationMeta.pendingHDSimulcast = null;
  };

  private toggleHDLayer = () => {
    if (!this.handle) {
      return;
    }

    const peerConnection = this.getPeerConnection();
    if (!peerConnection) {
      return;
    }

    const videoSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'video');

    if (videoSender) {
      const parameters = videoSender.getParameters();
      parameters.encodings[0].active = !parameters.encodings[0].active;

      logger
        .remote({ action: true })
        .log(
          `HD video toggled from ${parameters.encodings[0].active ? 'disabled' : 'enabled'} to ${
            parameters.encodings[0].active ? 'enabled' : 'disabled'
          }`
        );
      videoSender.setParameters(parameters);
    }
  };
}
